import { debounce } from "lodash";
import { useEffect, useState } from "react";
import {
  useConfigure,
  useHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { DynamicTranslationAlgolia } from "../types";

const initialPaginationModal = {
  pageSize: 25,
  page: 0,
};

type DynamicTranslationsProps = {
  contentItemFilterValues: string[];
  contentFilterValues: string[];
};

export const useDynamicTranslationsData = ({
  contentFilterValues,
  contentItemFilterValues,
}: DynamicTranslationsProps) => {
  const { query, refine: refineSearch, clear } = useSearchBox();
  const { hits, results } = useHits<DynamicTranslationAlgolia>();
  const { status } = useInstantSearch();
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModal,
  );

  // Clear search query on unmount custom hook
  useEffect(() => {
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalized table list data
  const data = hits
    .filter((hit) => !hit.isRTE)
    .map((hit) => {
      const flatTranslations = hit.translations
        ? Object.entries(hit.translations).reduce((acc, [locale, value]) => {
            return {
              ...acc,
              [`translations.${locale}`]: value,
            };
          }, {})
        : {};

      return {
        ...hit,
        ...flatTranslations,
        id: hit.objectID,
      };
    });

  const { nbHits = 0 } = results || {};
  const isLoading = status === "loading";

  // Filters settings
  const algoliaContentItemFilter =
    contentItemFilterValues.length > 0
      ? `(${contentItemFilterValues.map((value) => `contentItem:"${value}"`).join(" OR ")})`
      : "";

  const algoliaContentFilter =
    contentFilterValues.length > 0
      ? `(${contentFilterValues.map((value) => `content:"${value}"`).join(" OR ")})`
      : "";

  const filters = [algoliaContentItemFilter, algoliaContentFilter]
    .filter(Boolean)
    .join(" AND ");

  useConfigure({
    query,
    hitsPerPage: paginationModel.pageSize,
    page: paginationModel.page,
    filters,
  });

  const onResetPage = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onSearch = debounce((value) => {
    refineSearch(value);
    onResetPage();
  }, 500);

  return {
    searchData: {
      value: query,
      onChange: onSearch,
    },
    tableData: {
      data,
      rowCount: nbHits,
      paginationModel: paginationModel,
      onPaginationModelChange: setPaginationModel,
    },
    isLoading,
    onResetPage,
  };
};
